import {
  loadPdfImages,
  getPdfHeader,
  getPdfFooter,
  PDF_PAGE_MARGINS,
  createAndOpenPdf,
} from "../../../utility/pdfUtils";
import { getCompanyProfile } from "../../../Services/Services";

function numberToWords(num) {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000)
      return (
        a[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " " + inWords(n % 100) : "")
      );
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 ? " " + inWords(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 ? " " + inWords(n % 100000) : "")
      );
    return (
      inWords(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 ? " " + inWords(n % 10000000) : "")
    );
  }

  if (num === 0) return "Zero Rupees Only";

  const parts = num.toString().split(".");
  let words = inWords(parseInt(parts[0])) + " Rupees";
  if (parts[1]) {
    const paise = inWords(parseInt(parts[1].substring(0, 2)));
    if (paise) words += " and " + paise + " Paise";
  }
  return words + " Only";
}

// ── Helper: Info Grid Row (label right-aligned, colon fixed, value capped to fit cell) ──
const infoRow = (label, value, options = {}) => {
  const {
    bold = false,
    color = "#000",
    fontSize = 7.5,
    labelWidth = 48,
  } = options;
  return {
    columns: [
      {
        text: label,
        fontSize,
        color: "#333",
        width: labelWidth,
        alignment: "left",
      },
      { text: ":", fontSize, color: "#333", width: 6, alignment: "center" },
      { text: value || "N/A", fontSize, bold, color, width: "*" },
    ],
    columnGap: 3,
    margin: [0, 1, 0, 1],
  };
};

// ─── Main Export ───
export const generatePdf = async (order, action = "open") => {
  // ✅ Parallel fetch - images + company profile
  const [{ headerBase64, footerBase64 }, company] = await Promise.all([
    loadPdfImages(),
    getCompanyProfile(),
  ]);

  const items = Array.isArray(order?.items) ? order.items : [];
  const hasItems = items.length > 0;
  const party = order?.party_details || {};

  const subtotal = order.total_amount || 0;
  const cgstAmount =
    order.cgst_per && subtotal ? (subtotal * order.cgst_per) / 100 : 0;
  const sgstAmount =
    order.sgst_per && subtotal ? (subtotal * order.sgst_per) / 100 : 0;
  const roundOff = order.round_off || 0;
  const grand_total = subtotal + cgstAmount + sgstAmount + roundOff;

  const formatAmount = (num) => {
    if (!num) return "0.00";
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB");
  };

  const estimateDescriptionLines = (itemName, description) => {
    let lines = 1;
    if (description && description.trim()) {
      lines += Math.ceil(description.length / 50);
    }
    return Math.max(lines, 2);
  };

  const vCenterByLines = (text, totalLines, align = "center", fontSize = 7) => {
    const lineHeight = fontSize * 1.2;
    const totalHeight = totalLines * lineHeight;
    const marginTop = Math.max(0, (totalHeight - lineHeight) / 2);
    return {
      text,
      alignment: align,
      fontSize,
      margin: [0, marginTop, 0, marginTop],
    };
  };

  const getPriorityColor = (priority) => {
    const p = (priority || "").toLowerCase();
    if (p === "high") return "#d32f2f";
    if (p === "low") return "#f59e0b";
    return "#000000";
  };

  // ✅ Item Table Body - 11 columns
  const itemTableBody = hasItems
    ? [
        [
          { text: "S.No", bold: true, fontSize: 7, alignment: "center" },
          { text: "Category", bold: true, fontSize: 7, alignment: "center" },
          {
            text: "Product Type",
            bold: true,
            fontSize: 7,
            alignment: "center",
          },
          
          {
            text: "Item Description",
            bold: true,
            fontSize: 7,
            alignment: "center",
          },
          { text: "UOM", bold: true, fontSize: 7, alignment: "center" },
          { text: "Qty", bold: true, fontSize: 7, alignment: "center" },
          { text: "Rate", bold: true, fontSize: 7, alignment: "center" },
          { text: "Disc%", bold: true, fontSize: 7, alignment: "center" },
          { text: "CGST+SGST", bold: true, fontSize: 7, alignment: "center" },
          { text: "IGST", bold: true, fontSize: 7, alignment: "center" },
          { text: "Amount", bold: true, fontSize: 7, alignment: "center" },
        ],
        ...items.map((item, index) => {
          const rowCgstAmt =
            order.cgst_per && item.amount
              ? (item.amount * order.cgst_per) / 100
              : 0;
          const rowSgstAmt =
            order.sgst_per && item.amount
              ? (item.amount * order.sgst_per) / 100
              : 0;
          const descLines = estimateDescriptionLines(
            item.item_name,
            item.description,
          );

          return [
            vCenterByLines(`${index + 1}`, descLines, "center", 6.5),
            vCenterByLines(item.category_name || "", descLines, "center", 6.5),
            vCenterByLines(item.size_name || "", descLines, "center", 6.5),
            {
              stack: [
                { text: item.item_name || "", bold: true, fontSize: 6.5 },
                { text: item.description || "", fontSize: 6, italics: true },
              ],
              margin: [2, 2],
            },
            vCenterByLines(item.uom || "", descLines, "center", 6.5),
            vCenterByLines(
              item.quantity != null ? item.quantity.toString() : "",
              descLines,
              "center",
              6.5,
            ),
            vCenterByLines(
              item.rate != null && !isNaN(item.rate)
                ? formatAmount(item.rate)
                : "0.00",
              descLines,
              "center",
              6.5,
            ),
            vCenterByLines(
              item.dis_per != null && !isNaN(item.dis_per)
                ? Number(item.dis_per).toFixed(2)
                : "0.00",
              descLines,
              "center",
              6.5,
            ),
            vCenterByLines(formatAmount(rowCgstAmt), descLines, "center", 6.5),
            vCenterByLines(formatAmount(rowSgstAmt), descLines, "center", 6.5),
            vCenterByLines(
              item.amount != null && !isNaN(item.amount)
                ? formatAmount(item.amount)
                : "0.00",
              descLines,
              "center",
              6.5,
            ),
          ];
        }),
      ]
    : [];

  const terms = Array.isArray(order.terms_and_conditions)
    ? order.terms_and_conditions
    : [];

  const termsTableBody = [
    [
      { text: "ID", bold: true, fontSize: 7 },
      { text: "Point", bold: true, fontSize: 7 },
      { text: "Description", bold: true, fontSize: 7 },
    ],
    ...terms.map((term, index) => [
      { text: `${index + 1}`, alignment: "center", fontSize: 7 },
      { text: term.key || "", alignment: "left", fontSize: 7 },
      { text: term.value || "", alignment: "left", fontSize: 7 },
    ]),
  ];

  const rupeesInWords = numberToWords(Number(grand_total));

  // ✅ Combined Table Body - 11 columns
  let combinedTableBody = [];

  if (hasItems) {
    combinedTableBody = [
      ...itemTableBody,
      [
        { text: "", colSpan: 9, border: [false, false, false, false] },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        { text: "Subtotal (Rs.)", alignment: "right", fontSize: 7 },
        { text: formatAmount(subtotal), alignment: "right", fontSize: 7 },
      ],
      [
        { text: "", colSpan: 9, border: [false, false, false, false] },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {
          text: `CGST+SGST @ ${order.cgst_per || 0}%`,
          alignment: "right",
          fontSize: 7,
        },
        { text: formatAmount(cgstAmount), alignment: "right", fontSize: 7 },
      ],
      [
        { text: "", colSpan: 9, border: [false, false, false, false] },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {
          text: `IGST @ ${order.sgst_per || 0}%`,
          alignment: "right",
          fontSize: 7,
        },
        { text: formatAmount(sgstAmount), alignment: "right", fontSize: 7 },
      ],
      [
        { text: "", colSpan: 9, border: [false, false, false, false] },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        { text: "Round Off", alignment: "right", fontSize: 7 },
        { text: formatAmount(roundOff), alignment: "right", fontSize: 7 },
      ],
      [
        { text: "", colSpan: 9, border: [false, false, false, false] },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        { text: "Total (Rs.)", bold: true, alignment: "right", fontSize: 7 },
        {
          text: formatAmount(grand_total),
          bold: true,
          alignment: "right",
          fontSize: 7,
        },
      ],
      [
        {
          text: `Totals (In Words): ${rupeesInWords}`,
          colSpan: 9,
          alignment: "left",
          fontSize: 7,
          margin: [0, -30, 0, -25],
          border: [false, false, false, false],
        },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        { text: "", border: [false, false, false, false] },
        { text: "", border: [false, false, false, false] },
      ],
    ];
  }

  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: PDF_PAGE_MARGINS,
    defaultStyle: { fontSize: 9 },
    header: getPdfHeader(headerBase64),
    footer: getPdfFooter(footerBase64, false),

    content: [
      // ═══════════════════════════════════════
      // ✅ TITLE
      // ═══════════════════════════════════════
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "MOS",
                bold: true,
                alignment: "center",
                fontSize: 10,
                margin: [0, 3, 0, 3],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#000",
          vLineColor: () => "#000",
        },
        margin: [0, 0, 0, 0],
      },

      // ═══════════════════════════════════════
      // ✅ INFO GRID - Company from API (single unified table, uniform height)
      // ═══════════════════════════════════════
      {
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              // ── Column 1: Supplier ──
              {
                stack: [
                  {
                    text: "SUPPLIER",
                    bold: true,
                    fontSize: 8,
                    fillColor: "#f0f0f0",
                    margin: [2, 3, 2, 3],
                    alignment: "center",
                  },
                  infoRow("Company", company?.company_name || "", {
                    labelWidth: 48,
                    bold: true,
                  }),
                  infoRow("Address", company?.address || "", {
                    labelWidth: 48,
                  }),
                  infoRow("Email", company?.email || "", { labelWidth: 48 }),
                  infoRow("GSTIN", company?.gstin || "", { labelWidth: 48 }),
                  infoRow("PAN", company?.pan || "", { labelWidth: 48 }),
                  infoRow("Phone", company?.phone || "", { labelWidth: 48 }),
                ],
                margin: [3, 2, 3, 2],
              },

              // ── Column 2: Order Details ──
              {
                stack: [
                  {
                    text: "ORDER DETAILS",
                    bold: true,
                    fontSize: 8,
                    fillColor: "#f0f0f0",
                    margin: [2, 3, 2, 3],
                    alignment: "center",
                  },
                  infoRow("MOS", order.project_name, {
                    labelWidth: 48,
                    bold: true,
                  }),
                  infoRow("S.O. No", order.so_no, { labelWidth: 48 }),
                  infoRow("S.O. Date", formatDate(order.created_at), {
                    labelWidth: 48,
                  }),
                  infoRow("P.O. No", order.po_number, { labelWidth: 48 }),
                  infoRow("P.O. Date", formatDate(order.order_date), {
                    labelWidth: 48,
                  }),
                  infoRow("Delivery", formatDate(order.delivery_date), {
                    labelWidth: 48,
                  }),
                  infoRow("BOM", order.bom_name, { labelWidth: 48 }),
                  infoRow("Priority", order.priority || "Normal", {
                    labelWidth: 48,
                    bold: true,
                    color: getPriorityColor(order.priority),
                  }),
                  infoRow("Voltage", order.voltage_ratio, { labelWidth: 48 }),
                  infoRow("Rating", order.rating_specification, {
                    labelWidth: 48,
                  }),
                  infoRow("Winding", order.winding_material, {
                    labelWidth: 48,
                  }),
                ],
                margin: [3, 2, 3, 2],
              },

              // ── Column 3: Buyer ──
              {
                stack: [
                  {
                    text: "BUYER",
                    bold: true,
                    fontSize: 8,
                    fillColor: "#f0f0f0",
                    margin: [2, 3, 2, 3],
                    alignment: "center",
                  },
                  infoRow("Company", party.company_name, {
                    labelWidth: 48,
                    bold: true,
                  }),
                  infoRow("Address", party.address, { labelWidth: 48 }),
                  infoRow("Contact", party.contact, { labelWidth: 48 }),
                  infoRow("GST", party.gst, { labelWidth: 48 }),
                  infoRow("Email", party.email, { labelWidth: 48 }),
                  infoRow("Name", party.name, { labelWidth: 48 }),
                ],
                margin: [3, 2, 3, 2],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#000",
          vLineColor: () => "#000",
        },
        margin: [0, 0, 0, 8],
      },

      // ═══════════════════════════════════════
      // ✅ ITEM TABLE - 11 columns
      // ═══════════════════════════════════════
      ...(hasItems
        ? [
            {
              table: {
                headerRows: 1,
                widths: [13, 38, 38, "*", 22, 20, 38, 22, 28, 35, 45],
                body: combinedTableBody,
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => "#000",
                vLineColor: () => "#000",
              },
              margin: [0, 0, 0, 10],
            },
          ]
        : []),

      // ═══════════════════════════════════════
      // ✅ TERMS & CONDITIONS
      // ═══════════════════════════════════════
      {
        text: "Terms & Conditions:",
        style: "subheader",
        margin: [0, hasItems ? 10 : 0, 0, 5],
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "*"],
          body: termsTableBody,
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#000",
          vLineColor: () => "#000",
        },
        margin: [0, 0, 0, 20],
      },

      // ═══════════════════════════════════════
      // ✅ SIGNATURE - Company name from API
      // ═══════════════════════════════════════
      {
        text: `Regards\nFor ${company?.company_name || "SIDDHARTH INDUSTRIES PVT. LTD."}\n\n\n(Authorised Signatory)`,
        alignment: "right",
        style: "subheader",
        margin: [0, 10, 0, 0],
      },
    ],

    styles: {
      subheader: {
        fontSize: 8,
        bold: true,
        decoration: "underline",
      },
    },
  };

  createAndOpenPdf(docDefinition, action, `MOS_${order?.so_no || "export"}`);
};
